import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { AttachmentPanel } from "./AttachmentPanel";
import { trpc } from "../../lib/trpc";

type FaultStatus = "open" | "resolved" | "converted" | "cancelled";
type Severity = "low" | "medium" | "high" | "critical";

const STATUS_META: Record<FaultStatus, { label: string; cls: string; icon: string }> = {
  open: { label: "Açık", cls: "b-warn", icon: "alertTriangle" },
  resolved: { label: "Çözüldü", cls: "b-success", icon: "check" },
  converted: { label: "Bakımda", cls: "b-info", icon: "wrench" },
  cancelled: { label: "İptal", cls: "", icon: "x" },
};
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const SEV_OPTS: Array<[Severity, string]> = [["low", "Düşük"], ["medium", "Orta"], ["high", "Yüksek"], ["critical", "Kritik"]];

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtMoney(n: number): string { return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n); }
function fmtDateTime(iso: string | null): string { return iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"; }
/** ISO → datetime-local input değeri (yerel duvar saati). */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function localToIso(v: string): string | null { return v ? new Date(v).toISOString() : null; }
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return <div><div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-500)" }}>{label}</div>{children}</div>;
}

interface Props { open: boolean; faultReportId: string | null; canEdit: boolean; onClose: () => void; }

/**
 * Arıza kaydı detay + sonradan (çözülmüş olsa da, geriye dönük) zenginleştirme:
 * alan düzenleme + yedek parça tüketimi (FEFO stok düşümü) + doküman/resim ekleme.
 */
export function FaultReportDrawer({ open, faultReportId, canEdit, onClose }: Props) {
  const qc = useQueryClient();
  const getQ = trpc.faultReport.get.useQuery({ id: faultReportId ?? "" }, { enabled: open && !!faultReportId, staleTime: 3_000 });
  const updateMut = trpc.faultReport.update.useMutation();
  const resolveMut = trpc.faultReport.resolve.useMutation();
  const statusMut = trpc.faultReport.updateStatus.useMutation();
  const addPartMut = trpc.faultReport.addPart.useMutation();
  const removePartMut = trpc.faultReport.removePart.useMutation();
  const partsCatalogQ = trpc.inventory.stockOverview.useQuery({ page: 1, pageSize: 100, kinds: ["stock"] }, { enabled: open && canEdit, staleTime: 30_000 });

  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // edit form
  const [eLabel, setELabel] = useState(""); const [eDesc, setEDesc] = useState(""); const [eSev, setESev] = useState<Severity | "">("");
  const [eStopped, setEStopped] = useState(false); const [eStarted, setEStarted] = useState(""); const [eEst, setEEst] = useState(""); const [eDur, setEDur] = useState(""); const [eNotes, setENotes] = useState("");
  // part add
  const [partSel, setPartSel] = useState<string | null>(null); const [partQty, setPartQty] = useState(""); const [partAt, setPartAt] = useState("");
  // resolve dialog
  const [resolvePrompt, setResolvePrompt] = useState(false); const [rDur, setRDur] = useState(""); const [rNotes, setRNotes] = useState(""); const [rAt, setRAt] = useState("");

  useEffect(() => { if (open) { setErr(null); setEditing(false); setPartSel(null); setPartQty(""); setPartAt(""); setResolvePrompt(false); setRDur(""); setRNotes(""); setRAt(""); } }, [open, faultReportId]);

  const f = getQ.data;
  const meta = f ? STATUS_META[f.status as FaultStatus] : null;

  const partOptions = useMemo(() => {
    const rows = (partsCatalogQ.data?.rows ?? []) as Array<{ id: string; code: string; name: string; uom: string; onHand: number }>;
    return rows.map((r) => ({ value: r.id, label: r.name, desc: r.code, tail: `${fmtQty(r.onHand)} ${r.uom}` }));
  }, [partsCatalogQ.data]);

  function startEdit() {
    if (!f) return;
    setELabel(f.faultLabel); setEDesc(f.description ?? ""); setESev((f.severity as Severity) ?? "");
    setEStopped(f.productionStopped); setEStarted(toLocalInput(f.startedAt));
    setEEst(f.estimatedDurationMin != null ? String(f.estimatedDurationMin) : ""); setEDur(f.durationMin != null ? String(f.durationMin) : "");
    setENotes(f.resolutionNotes ?? ""); setEditing(true); setErr(null);
  }
  async function saveEdit() {
    if (!f) return; setErr(null);
    if (!eLabel.trim()) return setErr("Arıza tanımı zorunlu.");
    try {
      await updateMut.mutateAsync({
        id: f.id, faultLabel: eLabel.trim(), description: eDesc.trim() || null, severity: eSev || null,
        productionStopped: eStopped, startedAt: localToIso(eStarted) ?? undefined,
        estimatedDurationMin: eEst.trim() ? Math.max(0, Math.floor(Number(eEst))) : null,
        durationMin: eDur.trim() ? Math.max(0, Math.floor(Number(eDur))) : null,
        resolutionNotes: eNotes.trim() || null,
      });
      await qc.invalidateQueries(); setEditing(false);
    } catch (e) { setErr((e as Error).message); }
  }
  async function addPart() {
    if (!f || !partSel) return; setErr(null);
    const n = partQty.trim() ? parseTrNumber(partQty) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin.");
    try {
      await addPartMut.mutateAsync({ faultReportId: f.id, itemId: partSel, qty: n, at: partAt ? localToIso(partAt) : undefined });
      await qc.invalidateQueries(); setPartSel(null); setPartQty(""); setPartAt("");
    } catch (e) { setErr((e as Error).message); }
  }
  async function doResolve() {
    if (!f) return; setErr(null);
    try {
      await resolveMut.mutateAsync({ id: f.id, durationMin: rDur.trim() ? Math.max(0, Math.floor(Number(rDur))) : null, resolutionNotes: rNotes.trim() || null, resolvedAt: rAt ? localToIso(rAt) : undefined });
      await qc.invalidateQueries(); setResolvePrompt(false);
    } catch (e) { setErr((e as Error).message); setResolvePrompt(false); }
  }
  async function reopen() { if (!f) return; try { await statusMut.mutateAsync({ id: f.id, status: "open" }); await qc.invalidateQueries(); } catch (e) { setErr((e as Error).message); } }
  async function cancelFault() { if (!f) return; try { await statusMut.mutateAsync({ id: f.id, status: "cancelled" }); await qc.invalidateQueries(); } catch (e) { setErr((e as Error).message); } }

  const busy = updateMut.isPending || resolveMut.isPending || statusMut.isPending || addPartMut.isPending || removePartMut.isPending;
  const isConverted = f?.status === "converted";
  // Dönüştürülmüş arıza için: bağlı bakım emri kapandıysa (tamamlandı/iptal) arıza buradan çözülebilir
  // (backend `resolve` de aktif emir yoksa izin verir → satır aksiyonuyla tutarlı).
  const orderClosed = !f?.maintenanceOrder || f.maintenanceOrder.status === "completed" || f.maintenanceOrder.status === "cancelled";

  return (
    <Drawer
      open={open} onClose={onClose} size="xl"
      title={f ? `Arıza — ${f.faultLabel}` : "Arıza Kaydı"}
      subtitle={f ? `${f.centerName ?? "Tesis Geneli"}${f.locationName ? ` · ${f.locationName}` : ""}` : undefined}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      {getQ.isLoading || !f ? (
        <div style={{ padding: 8, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Durum + aksiyonlar */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {meta ? <span className={`badge-pill ${meta.cls}`}><Icon name={meta.icon} size={12} />{meta.label}</span> : null}
            {f.severity ? <span className="badge-pill">{SEV_LABELS[f.severity as Severity]}</span> : null}
            {f.productionStopped ? <span className="badge-pill b-danger"><Icon name="alertTriangle" size={12} />Üretim Durdu</span> : null}
            <span style={{ flex: 1 }} />
            {canEdit ? (
              <div className="mo-actions">
                {!editing ? <button type="button" className="btn-md secondary" onClick={startEdit} disabled={busy}><Icon name="edit" size={14} />Düzenle</button> : null}
                {isConverted ? (orderClosed ? (
                  <button type="button" className="btn-md primary" onClick={() => { setRDur(f.durationMin != null ? String(f.durationMin) : ""); setRNotes(f.resolutionNotes ?? ""); setRAt(""); setResolvePrompt(true); }} disabled={busy} title="Bağlı emir kapandı — arızayı çözüldü işaretle"><Icon name="check" size={14} />Çözümle</button>
                ) : null) : f.status === "open" ? (
                  <>
                    <button type="button" className="btn-md primary" onClick={() => { setRDur(f.durationMin != null ? String(f.durationMin) : ""); setRNotes(f.resolutionNotes ?? ""); setRAt(""); setResolvePrompt(true); }} disabled={busy}><Icon name="check" size={14} />Çözümle</button>
                    <button type="button" className="btn-md danger-outline" onClick={cancelFault} disabled={busy}><Icon name="x" size={14} />İptal</button>
                  </>
                ) : (f.status === "resolved" || f.status === "cancelled") ? (
                  <button type="button" className="btn-md secondary" onClick={reopen} disabled={busy}><Icon name="refresh" size={14} />Yeniden Aç</button>
                ) : null}
              </div>
            ) : null}
          </div>

          {isConverted ? (
            <div className="form-banner-info">
              Bu arıza bir <b>bakım emrine</b> dönüştürülmüş{f.maintenanceOrder ? <> (<span className="mono">{f.maintenanceOrder.code}</span>)</> : ""}.{" "}
              {orderClosed
                ? <>Bağlı emir kapandı — arıza buradan çözülebilir; düzenleme/parça/ek de eklenebilir.</>
                : <>Durum işlemleri bakım emrinden yönetilir; buradan yalnız düzenleme/parça/ek eklenebilir.</>}
            </div>
          ) : null}

          {/* Bilgi ızgarası */}
          <div className="d-card">
            <div className="d-card-body tight" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, fontSize: 12.5 }}>
              <Info label="Ekipman">{f.locationName ? <>{f.locationName} {f.locationCode ? <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--neutral-500)" }}>{f.locationCode}</span> : null}</> : "Merkez geneli"}</Info>
              <Info label="Merkez">{f.centerName ?? "Tesis Geneli"}</Info>
              <Info label="Önem">{f.severity ? SEV_LABELS[f.severity as Severity] : "—"}</Info>
              <Info label="Üretim Durdu">{f.productionStopped ? "Evet" : "Hayır"}</Info>
              <Info label="Başlangıç">{fmtDateTime(f.startedAt)}</Info>
              <Info label="Süre">{f.durationMin != null ? <span style={{ fontFamily: "var(--font-mono)" }}>{f.durationMin} dk</span> : "—"}</Info>
              <Info label="Bildiren">{f.reportedBy ?? "—"}</Info>
              <Info label="Çözüm">{fmtDateTime(f.resolvedAt)}</Info>
            </div>
          </div>

          {f.description && !editing ? <div style={{ fontSize: 12.5, color: "var(--neutral-700)", whiteSpace: "pre-wrap" }}>{f.description}</div> : null}
          {f.resolutionNotes && !editing ? <div style={{ fontSize: 12.5, color: "var(--neutral-700)" }}><b>Çözüm:</b> {f.resolutionNotes}</div> : null}

          {/* Düzenle paneli */}
          {editing ? (
            <div className="d-card" style={{ padding: 14 }}>
              <div className="form-field">
                <label className="lbl">Arıza Tanımı <span className="req">*</span></label>
                <input className="form-input" value={eLabel} onChange={(e) => setELabel(e.target.value)} maxLength={160} />
              </div>
              <div className="form-row">
                <div className="form-field"><label className="lbl">Önem</label><select className="form-select" value={eSev} onChange={(e) => setESev(e.target.value as Severity | "")}><option value="">—</option>{SEV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div className="form-field"><label className="lbl">Başlangıç</label><input className="form-input" type="datetime-local" value={eStarted} onChange={(e) => setEStarted(e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label className="lbl">Tahmini Süre (dk)</label><input className="form-input" type="number" min={0} value={eEst} onChange={(e) => setEEst(e.target.value)} /></div>
                <div className="form-field"><label className="lbl">Gerçekleşen Süre (dk)</label><input className="form-input" type="number" min={0} value={eDur} onChange={(e) => setEDur(e.target.value)} /></div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer", margin: "2px 0 10px" }}><input type="checkbox" checked={eStopped} onChange={(e) => setEStopped(e.target.checked)} />Üretim durdu</label>
              <div className="form-field"><label className="lbl">Açıklama / Detay</label><textarea className="form-textarea" rows={2} value={eDesc} onChange={(e) => setEDesc(e.target.value)} maxLength={1000} /></div>
              <div className="form-field"><label className="lbl">Çözüm Notu</label><textarea className="form-textarea" rows={2} value={eNotes} onChange={(e) => setENotes(e.target.value)} maxLength={1000} /></div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn-md ghost" onClick={() => setEditing(false)} disabled={updateMut.isPending}>Vazgeç</button>
                <button type="button" className="btn-md primary" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={14} />{updateMut.isPending ? "Kaydediliyor…" : "Kaydet"}</button>
              </div>
            </div>
          ) : null}

          {/* Yedek parçalar (stok düşümü) */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span className="mo-sec-title">Kullanılan Yedek Parçalar ({f.parts.length})</span>
              {f.partsCost > 0 ? <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--neutral-500)" }}>Σ {fmtMoney(f.partsCost)} ₺</span> : null}
            </div>
            {f.parts.length > 0 ? (
              <table className="dt2" style={{ marginBottom: 8 }}>
                <thead><tr><th>Parça</th><th>Lot</th><th style={{ width: 120 }}>Tarih</th><th className="num">Miktar</th><th className="num">Birim Maliyet</th><th className="num">Tutar</th>{canEdit ? <th style={{ width: 44 }} /> : null}</tr></thead>
                <tbody>
                  {f.parts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{p.itemCode}</span></td>
                      <td className="cell-id" style={{ fontSize: 11 }}>{p.lotCode ?? "—"}</td>
                      <td style={{ fontSize: 11.5 }}>{fmtDateTime(p.at)}</td>
                      <td className="cell-num">{fmtQty(p.qty)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{p.uom}</span></td>
                      <td className="cell-num">{p.unitCost != null ? fmtMoney(p.unitCost) : "—"}</td>
                      <td className="cell-num">{p.cost != null ? fmtMoney(p.cost) : "—"}</td>
                      {canEdit ? (
                        <td><div className="row-acts"><button type="button" className="iact danger" aria-label="Parçayı iade et" title="İade et (stok geri girer)" onClick={() => removePartMut.mutate({ partId: p.id }, { onSuccess: () => qc.invalidateQueries(), onError: (e) => setErr(e.message) })} disabled={removePartMut.isPending}><Icon name="x" size={14} /></button></div></td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: "10px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", marginBottom: 8 }}>Henüz parça girilmemiş.</div>
            )}
            {canEdit ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 220px", minWidth: 180 }}><SearchSelect value={partSel} options={partOptions} onChange={setPartSel} placeholder="Yedek parça / stok kartı seç…" searchPlaceholder="Parça ara…" icon="boxes" /></div>
                <input className="form-input" style={{ width: 96 }} type="text" inputMode="decimal" value={partQty} onChange={(e) => setPartQty(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="miktar" aria-label="Miktar" />
                <input className="form-input" style={{ width: 190 }} type="datetime-local" value={partAt} onChange={(e) => setPartAt(e.target.value)} aria-label="Tarih (geçmişe dönük)" title="Geçmişe dönük tarih (opsiyonel)" />
                <button type="button" className="btn-md primary" onClick={addPart} disabled={!partSel || addPartMut.isPending}><Icon name="plus" size={14} />{addPartMut.isPending ? "Düşülüyor…" : "Parça Kullan"}</button>
              </div>
            ) : null}
            {canEdit ? <div style={{ fontSize: 11, color: "var(--neutral-500)", marginTop: 4 }}>Stoktan FEFO ile düşülür; tarih boşsa bugün. İade edilirse stok geri girer. Çözülmüş arızada da eklenebilir.</div> : null}
          </div>

          {/* Ekler (foto/PDF) */}
          <AttachmentPanel faultReportId={f.id} canEdit={canEdit} />
        </div>
      )}

      <ConfirmDialog
        open={resolvePrompt}
        title="Arızayı çöz?"
        message={
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            <div className="form-row">
              <div className="form-field"><label className="lbl">Süre (dk)</label><input className="form-input" type="number" min={0} value={rDur} onChange={(e) => setRDur(e.target.value)} placeholder="gerçekleşen süre" /></div>
              <div className="form-field"><label className="lbl">Çözüm Tarihi</label><input className="form-input" type="datetime-local" value={rAt} onChange={(e) => setRAt(e.target.value)} title="Boşsa şimdi" /></div>
            </div>
            <div className="form-field"><label className="lbl">Çözüm Notu</label><textarea className="form-textarea" rows={2} value={rNotes} onChange={(e) => setRNotes(e.target.value)} maxLength={1000} placeholder="Ne yapıldı?" /></div>
          </div>
        }
        tone="primary" confirmLabel="Çözümle" cancelLabel="Vazgeç" busy={resolveMut.isPending}
        onConfirm={doResolve} onCancel={() => setResolvePrompt(false)}
      />
    </Drawer>
  );
}
